import { useEffect, useState } from 'react';
import { Plus, Euro, TrendingUp, RefreshCw, Tag, Target, Pencil, Trash2 } from 'lucide-react';
import { useAnalytics } from '../hooks/useAnalytics';
import { useExpenses } from '../hooks/useExpenses';
import { useRecurring } from '../hooks/useRecurring';
import { useAuth } from '../hooks/useAuth';
import { useCategories } from '../hooks/useCategories';
import { useGemmaAdvice } from '../hooks/useGemmaAdvice';
import { useGoals } from '../hooks/useGoals';
import { isPremiumPlan } from '../lib/plans';
import StatsCard from '../components/dashboard/StatsCard';
import UpcomingRecurring from '../components/dashboard/UpcomingRecurring';
import PaywallBanner from '../components/dashboard/PaywallBanner';
import BlurPreview from '../components/dashboard/BlurPreview';
import ExpenseList from '../components/expenses/ExpenseList';
import ExpenseForm from '../components/expenses/ExpenseForm';
import Modal from '../components/ui/Modal';
import Button from '../components/ui/Button';
import { getCategoryMeta } from '../hooks/useAnalytics';
import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import type { BudgetAlert, Goal } from '../types';
import Input from '../components/ui/Input';
import { supabase } from '../lib/supabase';

interface NotificationPrefs {
  emailRecurring?: boolean;
  pushRecurring?: boolean;
  recurringReminderOffsets?: number[];
}

const DASHBOARD_PERSONALIZATION: Record<
  'couples' | 'freelance' | 'personal',
  { title: string; description: string }
> = {
  couples: {
    title: 'Modalita Coppie attiva',
    description: 'Priorita su spese condivise, casa e intrattenimento.',
  },
  freelance: {
    title: 'Modalita Freelance attiva',
    description: 'Focus su cashflow, strumenti lavoro e abbonamenti business.',
  },
  personal: {
    title: 'Modalita Personale attiva',
    description: 'Panoramica semplice con obiettivi mensili individuali.',
  },
};

export default function Dashboard() {
  const { profile, user } = useAuth();
  const { categories } = useCategories();
  const { analytics, loading: analyticsLoading, refetch: refetchAnalytics } = useAnalytics();
  const { goals, loading: goalsLoading, addGoal, updateGoal, deleteGoal, refetch: refetchGoals } = useGoals();
  const {
    expenses,
    loading: expensesLoading,
    error,
    totalCount,
    createExpense,
    updateExpense,
    deleteExpense,
    refetch: refetchExpenses,
  } = useExpenses();
  const {
    templates: recurringTemplates,
    loading: recurringLoading,
    logNow: logNowRecurring,
    disableTemplate,
    refetch: refetchRecurring,
  } = useRecurring();
  const [addOpen, setAddOpen] = useState(false);
  const [goalModalOpen, setGoalModalOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [goalForm, setGoalForm] = useState({
    name: '',
    emoji: '🎯',
    target_amount: '',
    current_amount: '',
    deadline: '',
  });
  const [savingGoal, setSavingGoal] = useState(false);
  const [goalTopups, setGoalTopups] = useState<Record<string, string>>({});
  const isPremium = isPremiumPlan(profile?.plan);
  const { advice } = useGemmaAdvice(isPremium);
  const maxFreeCategories = 3;
  const usedFreeCategories = categories.length;
  const fallbackWasteYearly = analytics
    ? Math.max(0, analytics.currentMonthSpent * 0.08 * 12)
    : 0;
  const potentialWasteYearly = advice.wastePotentialYearly > 0
    ? advice.wastePotentialYearly
    : fallbackWasteYearly;
  const fallbackScannerTips = analytics
    ? [
        analytics.topCategory
          ? `La categoria ${getCategoryMeta(analytics.topCategory).label} e la principale: prova a ridurla del 10% nel prossimo mese.`
          : 'Controlla la categoria con maggior spesa e definisci un limite mensile.',
        analytics.monthlyChange > 10
          ? 'La spesa mensile sta accelerando: imposta un alert settimanale per non uscire budget.'
          : 'Mantieni il trend attuale e verifica le variazioni settimana su settimana.',
        analytics.recurringTotal > 0
          ? 'Rivedi gli addebiti ricorrenti attivi e disattiva quelli poco utilizzati.'
          : 'Traccia eventuali pagamenti periodici per migliorare la previsione dei prossimi mesi.',
      ]
    : [
        'Aggiungi qualche settimana di movimenti per ottenere rilevazioni piu affidabili.',
        'Classifica tutte le spese nelle categorie corrette per avere insight piu precisi.',
        'Imposta e conferma i pagamenti ricorrenti per una stima annuale piu solida.',
      ];
  const scannerTips = advice.tips.length
    ? advice.tips
    : fallbackScannerTips;
  const fakeScannerTips = [
    'Abbonamenti streaming potenzialmente ridondanti',
    'Picchi anomali su delivery nel weekend',
    'Spese micro ripetute in fascia serale',
  ];

  // Show only 5 most recent on dashboard
  const recentExpenses = expenses.slice(0, 5);

  const currentMonth = format(new Date(), 'MMMM yyyy');
  const onboardingUseCase = (profile as { onboarding_use_case?: 'couples' | 'freelance' | 'personal' | null } | null)
    ?.onboarding_use_case;
  const personalization = onboardingUseCase ? DASHBOARD_PERSONALIZATION[onboardingUseCase] : null;

  const getGoalAdvice = (goal: Goal): string => {
    const remaining = Math.max(0, goal.target_amount - goal.current_amount);
    if (remaining <= 0) return 'Goal raggiunto: ottimo lavoro.';

    const deadlineDate = goal.deadline ? new Date(goal.deadline) : null;
    const now = new Date();
    const monthsLeft = deadlineDate
      ? Math.max(1, Math.ceil((deadlineDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24 * 30)))
      : 1;
    const cutPerMonth = Number((remaining / monthsLeft).toFixed(0));
    const categoryLabel = analytics?.topCategory
      ? getCategoryMeta(analytics.topCategory).label
      : 'la categoria principale';
    return `Per raggiungere goal entro deadline, taglia €${cutPerMonth} da ${categoryLabel}.`;
  };

  useEffect(() => {
    if (!user) return;

    const handleBudgetAlertEvent = async (alert: BudgetAlert) => {
      const dedupeKey = `budgetai_budget_alert_seen_${alert.id}`;
      const lastSeen = Number(localStorage.getItem(dedupeKey) ?? '0');
      const currentExceeded = Number(alert.exceeded_amount ?? 0);

      if (Number.isFinite(lastSeen) && lastSeen >= currentExceeded) {
        return;
      }

      localStorage.setItem(dedupeKey, String(currentExceeded));

      const rawPrefs = (profile as unknown as { notification_prefs?: Record<string, boolean> | null })?.notification_prefs;
      const emailEnabled = Boolean(rawPrefs?.emailOverBudget);
      const pushEnabled = Boolean(rawPrefs?.pushOverBudget);

      const message = alert.message || `⚠️ Superato budget ${getCategoryMeta(alert.category).label} di €${alert.exceeded_amount.toFixed(2)}!`;

      if (pushEnabled && typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
        new Notification('BudgetAI', { body: message });
      } else {
        toast(message, { icon: '⚠️' });
      }

      if (emailEnabled) {
        try {
          await supabase.functions.invoke('send-alert-email', {
            body: { alertId: alert.id },
          });
        } catch {
          // Avoid breaking UI flow if email delivery fails.
        }
      }
    };

    const channel = supabase
      .channel(`budget-alerts:${user.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'budget_alerts', filter: `user_id=eq.${user.id}` },
        async (payload) => handleBudgetAlertEvent(payload.new as BudgetAlert),
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'budget_alerts', filter: `user_id=eq.${user.id}` },
        async (payload) => handleBudgetAlertEvent(payload.new as BudgetAlert),
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile, user]);

  useEffect(() => {
    const rawPrefs = (profile as unknown as { notification_prefs?: NotificationPrefs | null })?.notification_prefs;
    const prefs = rawPrefs ?? {};
    const recurringEnabled = Boolean(prefs.emailRecurring || prefs.pushRecurring);
    if (!recurringEnabled) return;

    const offsets = (prefs.recurringReminderOffsets?.length ? prefs.recurringReminderOffsets : [7, 5, 3, 1])
      .filter((v) => Number.isFinite(v) && v > 0);
    if (offsets.length === 0) return;

    const today = new Date();
    const dailyKey = `budgetai_recurring_notice_${format(today, 'yyyy-MM-dd')}`;
    const alreadyShown = new Set<string>(JSON.parse(localStorage.getItem(dailyKey) ?? '[]') as string[]);

    const toNotify: string[] = [];

    for (const t of recurringTemplates) {
      if (!t.next_due) continue;
      const due = parseISO(t.next_due);
      const diff = differenceInCalendarDays(due, today);
      if (!offsets.includes(diff)) continue;

      const marker = `${t.id}:${diff}`;
      if (alreadyShown.has(marker)) continue;

      toNotify.push(marker);
      const message = `Promemoria: "${t.description}" scade tra ${diff}g`;

      if (prefs.pushRecurring && typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
        new Notification('BudgetAI', { body: message });
      } else {
        toast(message, { icon: '🔔' });
      }
    }

    if (toNotify.length > 0) {
      localStorage.setItem(dailyKey, JSON.stringify([...alreadyShown, ...toNotify]));
    }
  }, [recurringTemplates, profile]);

  const openCreateGoalModal = () => {
    setEditingGoal(null);
    setGoalForm({ name: '', emoji: '🎯', target_amount: '', current_amount: '', deadline: '' });
    setGoalModalOpen(true);
  };

  const openEditGoalModal = (goal: Goal) => {
    setEditingGoal(goal);
    setGoalForm({
      name: goal.name,
      emoji: goal.emoji || '🎯',
      target_amount: String(goal.target_amount),
      current_amount: String(goal.current_amount),
      deadline: goal.deadline ?? '',
    });
    setGoalModalOpen(true);
  };

  const saveGoal = async () => {
    const targetAmount = Number(goalForm.target_amount);
    const currentAmount = Number(goalForm.current_amount || 0);
    if (!goalForm.name.trim()) {
      toast.error('Inserisci nome goal');
      return;
    }
    if (!Number.isFinite(targetAmount) || targetAmount <= 0) {
      toast.error('Target non valido');
      return;
    }
    if (!Number.isFinite(currentAmount) || currentAmount < 0) {
      toast.error('Importo attuale non valido');
      return;
    }
    if (Array.from((goalForm.emoji || '').trim()).length > 8) {
      toast.error('Emoji troppo lunga');
      return;
    }

    setSavingGoal(true);
    try {
      const payload = {
        name: goalForm.name,
        emoji: goalForm.emoji || '🎯',
        target_amount: targetAmount,
        current_amount: currentAmount,
        deadline: goalForm.deadline || null,
      };

      if (editingGoal) {
        await updateGoal(editingGoal.id, payload);
        toast.success('Goal aggiornato');
      } else {
        await addGoal(payload);
        toast.success('Goal creato');
      }

      setGoalModalOpen(false);
      await refetchGoals();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Errore salvataggio goal');
    } finally {
      setSavingGoal(false);
    }
  };

  const removeGoal = async (goalId: string) => {
    try {
      await deleteGoal(goalId);
      toast.success('Goal eliminato');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Errore eliminazione goal');
    }
  };

  const addSavingsToGoal = async (goal: Goal) => {
    const amount = Number(goalTopups[goal.id] ?? '0');
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Inserisci un importo valido');
      return;
    }

    try {
      await updateGoal(goal.id, {
        name: goal.name,
        emoji: goal.emoji,
        target_amount: goal.target_amount,
        current_amount: Number((goal.current_amount + amount).toFixed(2)),
        deadline: goal.deadline,
      });
      setGoalTopups((prev) => ({ ...prev, [goal.id]: '' }));
      toast.success('Risparmio aggiunto al goal');
      await refetchGoals();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Errore aggiornamento risparmio');
    }
  };

  const handleCreateExpense = async (data: Parameters<typeof createExpense>[0]) => {
    const created = await createExpense(data);
    // Keep cards and recurring widget in sync even when realtime is delayed.
    await Promise.all([refetchAnalytics(), refetchRecurring()]);
    return created;
  };

  const handleLogNow = async (template: Parameters<typeof logNowRecurring>[0]) => {
    await logNowRecurring(template);
    await Promise.all([refetchAnalytics(), refetchExpenses(), refetchRecurring()]);
  };

  const handleDisableRecurring = async (template: Parameters<typeof disableTemplate>[0]) => {
    await disableTemplate(template);
    await Promise.all([refetchAnalytics(), refetchExpenses(), refetchRecurring()]);
  };

  return (
    <>
      {!isPremium && <PaywallBanner />}

      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-slate-400 text-sm mt-0.5">
            Good to see you, {profile?.full_name?.split(' ')[0] ?? 'there'} 👋
          </p>
          {!isPremium && (
            <p className="text-xs text-amber-300 mt-2">
              {usedFreeCategories}/{maxFreeCategories} categorie custom usate
            </p>
          )}
        </div>
        <Button
          onClick={() => setAddOpen(true)}
          leftIcon={<Plus size={16} />}
        >
          <span className="hidden sm:inline">Add Expense</span>
          <span className="sm:hidden">Add</span>
        </Button>
      </div>

      {personalization && (
        <div className="mb-6 rounded-xl border border-brand-900/60 bg-brand-900/20 p-4">
          <p className="text-sm font-semibold text-brand-300">{personalization.title}</p>
          <p className="mt-1 text-xs text-slate-300">{personalization.description}</p>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatsCard
          title={`Spent in ${currentMonth}`}
          value={analytics ? `€${analytics.currentMonthSpent.toFixed(2)}` : '—'}
          icon={<Euro size={18} className="text-brand-400" />}
          trend={analytics?.monthlyChange}
          trendLabel="vs last month"
          loading={analyticsLoading}
        />
        <StatsCard
          title="Total (12 months)"
          value={analytics ? `€${analytics.totalSpent.toFixed(2)}` : '—'}
          icon={<TrendingUp size={18} className="text-violet-400" />}
          iconBg="bg-violet-500/15"
          loading={analyticsLoading}
        />
        <StatsCard
          title="Recurring / month"
          value={analytics ? `€${analytics.recurringTotal.toFixed(2)}` : '—'}
          icon={<RefreshCw size={18} className="text-blue-400" />}
          iconBg="bg-blue-500/15"
          loading={analyticsLoading}
        />
        <StatsCard
          title="Top category"
          value={
            analytics?.topCategory
              ? getCategoryMeta(analytics.topCategory).label
              : '—'
          }
          icon={<Tag size={18} className="text-emerald-400" />}
          iconBg="bg-emerald-500/15"
          loading={analyticsLoading}
        />
      </div>

      {/* Scanner teaser */}
      <div className="card p-5 mb-6">
        <h2 className="text-base font-semibold text-slate-200 mb-1">Scanner</h2>
        <p className="text-amber-300 font-medium">
          Stai sprecando potenzialmente €{potentialWasteYearly.toFixed(0)}/anno
        </p>
        <div className="mt-4">
          <BlurPreview
            locked={!isPremium}
            fakeContent={(
              <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 space-y-3">
                <p className="text-sm text-slate-300">Dettagli principali rilevati:</p>
                <ul className="space-y-2 text-sm text-slate-400">
                  {fakeScannerTips.map((tip) => (
                    <li key={tip}>• {tip}</li>
                  ))}
                </ul>
              </div>
            )}
          >
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 space-y-3">
              <p className="text-sm text-slate-300">Dettagli principali rilevati:</p>
              <ul className="space-y-2 text-sm text-slate-400">
                {scannerTips.map((tip) => (
                  <li key={tip}>• {tip}</li>
                ))}
              </ul>
            </div>
          </BlurPreview>
        </div>
      </div>

      {/* Charts */}
      {/* {!!analytics && (analytics.byMonth.length > 0 || analytics.byCategory.length > 0) && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
          {analytics.byMonth.length > 0 && (
            <BlurPreview
              locked={!isPremium}
              fakeContent={(
                <div className="card p-5">
                  <h3 className="text-sm font-semibold text-slate-300 mb-5">Monthly Spending</h3>
                  <div className="h-[220px] rounded-xl bg-slate-900/70 p-4 flex items-end gap-2">
                    <div className="w-6 h-16 bg-brand-500/50 rounded-t" />
                    <div className="w-6 h-24 bg-brand-500/50 rounded-t" />
                    <div className="w-6 h-14 bg-brand-500/50 rounded-t" />
                    <div className="w-6 h-28 bg-brand-500/50 rounded-t" />
                    <div className="w-6 h-20 bg-brand-500/50 rounded-t" />
                    <div className="w-6 h-32 bg-brand-500/50 rounded-t" />
                  </div>
                </div>
              )}
            >
              <SpendingChart data={analytics.byMonth} />
            </BlurPreview>
          )}
          {analytics.byCategory.length > 0 && (
            <BlurPreview
              locked={!isPremium}
              fakeContent={(
                <div className="card p-5">
                  <h3 className="text-sm font-semibold text-slate-300 mb-5">Category Breakdown</h3>
                  <div className="flex items-center gap-6">
                    <div className="w-40 h-40 rounded-full bg-slate-800/80 border-8 border-slate-700/70" />
                    <div className="flex-1 space-y-3">
                      <div className="h-3 w-32 bg-slate-700/70 rounded" />
                      <div className="h-3 w-40 bg-slate-700/70 rounded" />
                      <div className="h-3 w-28 bg-slate-700/70 rounded" />
                      <div className="h-3 w-36 bg-slate-700/70 rounded" />
                    </div>
                  </div>
                </div>
              )}
            >
              <CategoryBreakdown data={analytics.byCategory} totalSpent={analytics.totalSpent} />
            </BlurPreview>
          )}
        </div>
      )} */}

      {/* Upcoming Recurring */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-slate-200 flex items-center gap-2">
            <RefreshCw size={15} className="text-brand-400" />
            Prossimi ricorrenti
          </h2>
          {recurringTemplates.length > 0 && (
            <span className="text-xs text-slate-500">
              {recurringTemplates.length} nei prossimi 30gg
            </span>
          )}
        </div>
        <UpcomingRecurring
          templates={recurringTemplates}
          loading={recurringLoading}
          onLogNow={handleLogNow}
          onDisableRecurring={handleDisableRecurring}
        />
      </div>

      {/* Savings goals */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-slate-200 flex items-center gap-2">
            <Target size={15} className="text-emerald-400" />
            Savings Goals
          </h2>
          {isPremium && (
            <Button size="sm" variant="secondary" onClick={openCreateGoalModal} leftIcon={<Plus size={14} />}>
              Nuovo goal
            </Button>
          )}
        </div>

        {!isPremium ? (
          <div className="rounded-xl border border-brand-800/50 bg-brand-900/15 p-4">
            <p className="text-sm text-slate-200">Savings Goals disponibili su Premium.</p>
            <p className="text-xs text-slate-400 mt-1">Crea obiettivi con progress bar e suggerimento AI per rispettare la deadline.</p>
          </div>
        ) : goalsLoading ? (
          <p className="text-xs text-slate-400">Caricamento goals...</p>
        ) : goals.length === 0 ? (
          <p className="text-xs text-slate-400">Nessun goal attivo. Crea il primo obiettivo.</p>
        ) : (
          <div className="space-y-3">
            {goals.map((goal) => {
              const progressRaw = goal.target_amount > 0 ? (goal.current_amount / goal.target_amount) * 100 : 0;
              const progress = Math.max(0, Math.min(100, Number(progressRaw.toFixed(1))));
              return (
                <div key={goal.id} className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-100">
                        {goal.name} {goal.emoji} €{goal.current_amount.toFixed(0)}/€{goal.target_amount.toFixed(0)} ({progress.toFixed(0)}%)
                      </p>
                      <p className="text-xs text-slate-400 mt-1">{getGoalAdvice(goal)}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => openEditGoalModal(goal)} leftIcon={<Pencil size={13} />}>
                        Modifica
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => removeGoal(goal.id)} leftIcon={<Trash2 size={13} />}>
                        Elimina
                      </Button>
                    </div>
                  </div>
                  <div className="mt-3 h-2 rounded-full bg-slate-800 overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: `${progress}%` }} />
                  </div>
                  <div className="mt-3 flex items-end gap-2">
                    <div className="w-36">
                      <Input
                        label="Aggiungi risparmio (€)"
                        type="number"
                        value={goalTopups[goal.id] ?? ''}
                        onChange={(e) => setGoalTopups((prev) => ({ ...prev, [goal.id]: e.target.value }))}
                        placeholder="es. 100"
                      />
                    </div>
                    <Button size="sm" variant="secondary" onClick={() => addSavingsToGoal(goal)}>
                      Aggiungi
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent expenses */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-slate-200">Recent Expenses</h2>
          <a href="/expenses" className="text-xs text-brand-400 hover:text-brand-300 transition-colors">
            View all ({totalCount})
          </a>
        </div>
        <ExpenseList
          expenses={recentExpenses}
          loading={expensesLoading}
          error={error}
          onUpdate={updateExpense}
          onDelete={deleteExpense}
        />
      </div>

      {/* Add expense modal */}
      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Add Expense">
        <ExpenseForm
          onCreate={handleCreateExpense}
          onSuccess={() => setAddOpen(false)}
          onCancel={() => setAddOpen(false)}
        />
      </Modal>

      <Modal
        open={goalModalOpen}
        onClose={() => setGoalModalOpen(false)}
        title={editingGoal ? 'Modifica goal' : 'Nuovo goal'}
      >
        <div className="space-y-3">
          <Input
            label="Nome"
            value={goalForm.name}
            onChange={(e) => setGoalForm((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="Vacanze"
          />
          <Input
            label="Emoji"
            value={goalForm.emoji}
            onChange={(e) => setGoalForm((prev) => ({ ...prev, emoji: e.target.value }))}
            placeholder="🏖️"
            maxLength={8}
          />
          <Input
            label="Target (€)"
            type="number"
            value={goalForm.target_amount}
            onChange={(e) => setGoalForm((prev) => ({ ...prev, target_amount: e.target.value }))}
          />
          <Input
            label="Importo attuale (€)"
            type="number"
            value={goalForm.current_amount}
            onChange={(e) => setGoalForm((prev) => ({ ...prev, current_amount: e.target.value }))}
          />
          <Input
            label="Deadline"
            type="date"
            value={goalForm.deadline}
            onChange={(e) => setGoalForm((prev) => ({ ...prev, deadline: e.target.value }))}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setGoalModalOpen(false)}>Annulla</Button>
            <Button onClick={saveGoal} loading={savingGoal}>Salva</Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
